// Janela de tempo do Dashboard do Faturamento.
//
// Diferente do filtro do Painel Executivo (src/lib/financeiro/periodo.ts), que
// recorta por `mes_referencia` com grão de MÊS: aqui o corte é por `emitido_em`,
// um instante, e o grão precisa ser de DIA — "últimos 30 dias" é a pergunta que o
// Faturamento faz sobre o próprio ritmo de trabalho.

export type PeriodoEmissao =
  | 'ultimos7'
  | 'ultimos30'
  | 'ultimos90'
  | 'mesAtual'
  | 'mesAnterior'
  | 'anoAtual'
  | 'historico';

export interface JanelaEmissao {
  /** Meia-noite local do primeiro dia, incluso. */
  inicio: Date;
  /** 23:59:59.999 local do último dia, incluso. */
  fim: Date;
}

export const OPCOES_PERIODO_EMISSAO: { value: PeriodoEmissao; label: string }[] = [
  { value: 'ultimos7', label: 'Últimos 7 dias' },
  { value: 'ultimos30', label: 'Últimos 30 dias' },
  { value: 'ultimos90', label: 'Últimos 90 dias' },
  { value: 'mesAtual', label: 'Mês atual' },
  { value: 'mesAnterior', label: 'Mês anterior' },
  { value: 'anoAtual', label: 'Ano atual' },
  { value: 'historico', label: 'Todo o histórico' },
];

export const rotuloPeriodoEmissao = (p: PeriodoEmissao): string =>
  OPCOES_PERIODO_EMISSAO.find((o) => o.value === p)?.label ?? 'Período atual';

const inicioDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const fimDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

/**
 * Janela do período, ou `null` para "todo o histórico" (sem recorte).
 *
 * As janelas relativas incluem hoje: "últimos 7 dias" são hoje e os 6 anteriores.
 */
export function calcularJanelaEmissao(periodo: PeriodoEmissao, hoje: Date): JanelaEmissao | null {
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const ultimosNDias = (n: number): JanelaEmissao => {
    const inicio = new Date(ano, mes, hoje.getDate() - (n - 1));
    return { inicio: inicioDoDia(inicio), fim: fimDoDia(hoje) };
  };

  switch (periodo) {
    case 'ultimos7': return ultimosNDias(7);
    case 'ultimos30': return ultimosNDias(30);
    case 'ultimos90': return ultimosNDias(90);
    case 'mesAtual':
      return { inicio: inicioDoDia(new Date(ano, mes, 1)), fim: fimDoDia(hoje) };
    case 'mesAnterior':
      return { inicio: inicioDoDia(new Date(ano, mes - 1, 1)), fim: fimDoDia(new Date(ano, mes, 0)) };
    case 'anoAtual':
      return { inicio: inicioDoDia(new Date(ano, 0, 1)), fim: fimDoDia(hoje) };
    case 'historico':
    default:
      return null;
  }
}

/** `true` quando o instante da emissão cai na janela. Janela nula aceita tudo. */
export function emissaoNaJanela(emitidoEm: string | null | undefined, janela: JanelaEmissao | null): boolean {
  if (!janela) return !!emitidoEm;
  if (!emitidoEm) return false;
  const d = new Date(emitidoEm);
  if (isNaN(d.getTime())) return false;
  return d >= janela.inicio && d <= janela.fim;
}
